import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TreatmentTemplateStats } from '../calendar/types';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../../config/api';
import { useThemedStyles } from '../../theme/useThemedStyles';

interface Props {
  token: string;
}

const fmtMoney = (value?: number | null) => `Bs ${Number(value || 0).toFixed(2)}`;
const fmtDuration = (value?: number | null) => value == null ? '-' : `${Number(value).toFixed(0)} min`;

export default function TreatmentUsageReport({ token }: Props) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [stats, setStats] = useState<TreatmentTemplateStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.TREATMENT_TEMPLATE_STATS), {
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error('Failed to load treatment statistics');
      const data = await response.json();
      setStats(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load treatment statistics');
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const totals = useMemo(() => ({
    appointments: stats.reduce((sum, item) => sum + item.appointmentCount, 0),
    completed: stats.reduce((sum, item) => sum + item.completedAppointmentCount, 0),
    cost: stats.reduce((sum, item) => sum + item.totalCost, 0)
  }), [stats]);

  return (
    <section style={{ ...theme.card, padding: 14 }}>
      <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, color: theme.text, fontSize: 16 }}>{t('treatmentCatalog.usageTitle', 'Treatment usage')}</h3>
          <div style={{ color: theme.muted, fontSize: 12, marginTop: 3 }}>{t('treatmentCatalog.usageHelp', 'Usage is based on appointments created from each treatment.')}</div>
        </div>
        <button type="button" onClick={loadStats} style={{ ...theme.secondaryButton, padding: '8px 10px' }}>
          {t('common.refresh')}
        </button>
      </div>

      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
        {[
          [t('treatmentCatalog.totalAppointments', 'Appointments'), totals.appointments],
          [t('treatmentCatalog.completedAppointments', 'Completed'), totals.completed],
          [t('treatmentCatalog.totalCost', 'Total cost'), fmtMoney(totals.cost)]
        ].map(([label, value]) => (
          <div key={String(label)} style={{ ...theme.panel, padding: 12 }}>
            <div style={{ color: theme.muted, fontSize: 12 }}>{label}</div>
            <div style={{ color: theme.text, fontSize: 22, fontWeight: 800 }}>{value}</div>
          </div>
        ))}
      </div>

      {loading && <div style={{ color: theme.muted }}>{t('common.loading')}</div>}
      {!!error && <div role="alert" style={{ color: '#fca5a5' }}>{error}</div>}
      {!loading && !error && stats.length === 0 && <div style={{ color: theme.muted }}>{t('treatmentCatalog.noUsage', 'No usage yet.')}</div>}

      <div style={{ display: 'grid', gap: 10 }}>
        {stats.map(item => (
          <div key={item.id} style={{ ...theme.panel, padding: 12 }}>
            <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 12, alignItems: 'center' }}>
              <div style={{ color: theme.text, fontWeight: 800 }}>{item.name}</div>
              <div>{item.completedAppointmentCount}/{item.appointmentCount}</div>
              <div>{fmtDuration(item.averageDurationMinutes)}</div>
              <div>{fmtMoney(item.averageCost)}</div>
            </div>
            {item.doctorStats.length > 0 && (
              <div className="responsive-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                {item.doctorStats.map(doctor => (
                  <div key={doctor.doctorId} style={{ border: `1px solid ${theme.border}`, borderRadius: 6, padding: '6px 8px', color: theme.muted, fontSize: 12 }}>
                    Dr. {doctor.firstName} {doctor.lastName}: {doctor.appointmentCount} | {fmtDuration(doctor.averageDurationMinutes)} | {fmtMoney(doctor.averageCost)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
